import React from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import ScanFeedCard from './ScanFeedCard'
import OralScoreFindingTooltip from './OralScoreFindingTooltip'
import { OralScoreCategories, OralScoreColorCodes, OralScores } from 'consts/oralScoreConsts'
import OralScoreFrozenIndicator from '../OralScoreFrozenIndicator'
import { Grid } from '@material-ui/core'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import BetaBadge from 'components/common/BetaBadge'
import Tooltip from 'components/common/Tooltip'
import InfoIcon from 'components/common/icons/Information'
import SparkleIcon from 'components/CopilotControl/icons/SparkleIcon'

const RISK_LEVELS = [OralScores.LowRisk, OralScores.MediumRisk, OralScores.HighRisk]
const HYGIENE_LEVELS = [OralScores.Excellent, OralScores.Good, OralScores.Fair, OralScores.Poor]

const FINDINGS = [
  {
    category: OralScoreCategories.PerioRisk,
    scoreKey: 'perioRisk',
    rawScoreKey: 'periodontal_risk',
    levels: RISK_LEVELS
  },
  { category: OralScoreCategories.DentalRisk, scoreKey: 'dentalRisk', rawScoreKey: 'dental_risk', levels: RISK_LEVELS },
  {
    category: OralScoreCategories.Hygiene,
    scoreKey: 'hygieneScore',
    rawScoreKey: 'oral_hygiene_score',
    levels: HYGIENE_LEVELS
  }
]

// GR-10779 Figma frame 219. The title keeps ScanFeedCard's 14px so it matches Scan Reviews and the other feed cards.
const useStyles = makeStyles(theme => ({
  betaBadge: {
    marginLeft: 14
  },
  // Figma spaces the dividers 24px apart, which only fits wide cards. The spacing shrinks with the card
  // so the three columns share one row down to a 1366px window with the chat open. Narrower, they wrap.
  scoreCategories: {
    rowGap: 12
  },
  scoreCategory: {
    borderRight: '1px solid #E1E6EA',
    paddingRight: 'clamp(12px, 4%, 24px)',
    marginRight: 'clamp(12px, 4%, 24px)',
    '&:last-child': {
      borderRight: '0px',
      paddingRight: 0,
      marginRight: 0
    }
  },
  categoryLabel: {
    display: 'flex',
    alignItems: 'center'
  },
  infoIcon: {
    display: 'flex',
    marginLeft: 4,
    cursor: 'pointer'
  },
  tooltip: {
    maxWidth: '320px !important',
    padding: '15px !important',
    borderRadius: 10
  },
  scoreValue: {
    display: 'flex',
    alignItems: 'center',
    gap: 7
  }
}))

const AI_MARKER_STYLE = { width: 9.5, height: 9.5, flexShrink: 0, marginLeft: 4 }

const OralScoresCard = ({ oralScores = {}, rawOralScores = {} }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  return (
    <ScanFeedCard
      id="scan-feed-oral-scores-card"
      title={
        <Grid container direction="row" alignItems="center" wrap="nowrap">
          <Grid item>{t('pages.patients.selectedPatient.timelineV2.oralScores.findingsCard.title')}</Grid>
          <Grid item>
            <BetaBadge className={classes.betaBadge} />
          </Grid>
        </Grid>
      }
    >
      <Grid container className={classes.scoreCategories}>
        {FINDINGS.map(({ category, scoreKey, rawScoreKey, levels }) => (
          <Grid item key={category} className={classes.scoreCategory}>
            <DazzedParagraph14 strong className={classes.categoryLabel} color="var(--text-color-6)">
              {t(`pages.patients.selectedPatient.timelineV2.oralScores.findingsCard.categories.${category}`)}
              <Tooltip
                value={<OralScoreFindingTooltip category={category} levels={levels} />}
                placement="top"
                arrow
                variant="light"
                containerClassName={classes.infoIcon}
                tooltipContentClassName={classes.tooltip}
              >
                <InfoIcon color="#E1E6EA" width={15} height={15} />
              </Tooltip>
            </DazzedParagraph14>
            <DazzedParagraph14 bold className={classes.scoreValue} color={OralScoreColorCodes[oralScores[scoreKey]]}>
              <SparkleIcon color="#8B99B7" style={AI_MARKER_STYLE} />
              {t(`pages.patients.selectedPatient.timelineV2.oralScores.scores.${oralScores[scoreKey]}`)}
            </DazzedParagraph14>
            <OralScoreFrozenIndicator oralScores={rawOralScores} scoreKey={rawScoreKey} />
          </Grid>
        ))}
      </Grid>
    </ScanFeedCard>
  )
}

export default OralScoresCard
